/**
 * Shared, isomorphic property types: the auto-carried property set, URL and
 * param sanitisation helpers, and the route→feature maps.
 *
 * Nothing in here may import an SDK — this file is loaded by both the browser
 * and Node entrypoints. See the header of `index.ts`.
 */

/**
 * PostHog ingestion host. Lives HERE, not in `client.ts`, because `server.ts`
 * needs it too and `client.ts` imports posthog-js — a value import across that
 * boundary would pull the browser SDK into every Node build that touches
 * `server.ts` (see this package's `index.ts` header, and the `client.ts`
 * exclude in `apps/agent/tsconfig.json`). This file imports no SDK, so both
 * entrypoints can share it and the region stays a single source of truth.
 */
export const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

export type AppName = 'student' | 'parent';

/** Where the app is running. Native surfaces come from Capacitor. */
export type Surface = 'web' | 'ios' | 'android';

/** docs/plans/analytics-implementation-plan.md §6.3. */
export type StudentFeature =
  'onboarding' | 'learn' | 'practice' | 'exam' | 'leaderboard' | 'ama' | 'scan' | 'profile';

/** docs/plans/analytics-implementation-plan.md §7.1. */
export type ParentFeature =
  'onboarding' | 'children' | 'child_hub' | 'reports' | 'homework' | 'exam' | 'news' | 'profile';

export type Feature = StudentFeature | ParentFeature;

/**
 * Never declare these in an individual event's `EventMap` entry
 * (docs/plans/analytics-implementation-plan.md §5.1).
 *
 * On the client, `capture()`/`capturePageview()` attach the full set to EVERY
 * event via `autoProperties()` (`client.ts`). On the server, only `app` is
 * enforced — `TrackArgs.properties` requires it explicitly and the caller
 * supplies it; the rest of `AutoProperties` is not auto-populated server-side.
 */
export interface AutoProperties {
  app: AppName;
  surface: Surface;
  app_version: string;
  feature?: Feature;
  days_since_signup?: number;
  is_first_session?: boolean;
}

/**
 * First-touch attribution (the `utm_*` tags and referrer of a person's first
 * visit). Lives here, not in `first-touch.ts`, because the API request
 * schemas and `EventMap` need the shape and `first-touch.ts` reads `window`.
 * The clients send it in the account-creating request and the API stamps it
 * on the signup event (`signup_completed` / `parent_signup_completed`).
 */
export interface FirstTouch {
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_utm_term?: string;
  first_utm_content?: string;
  /** Referrer origin + path; query and hash are dropped. */
  first_referrer?: string;
  /** Referrer hostname, e.g. `www.google.com`. */
  first_referring_domain?: string;
}

export const FIRST_TOUCH_KEYS: readonly (keyof FirstTouch)[] = [
  'first_utm_source',
  'first_utm_medium',
  'first_utm_campaign',
  'first_utm_term',
  'first_utm_content',
  'first_referrer',
  'first_referring_domain',
];

// Campaign tags are attacker-controllable free text that ends up on a person
// profile and a signup event; keep them bounded on both sides of the wire.
export const FIRST_TOUCH_MAX_LENGTH = 200;

/**
 * JSON schema for the `first_touch` request-body field: known keys only,
 * strings only, bounded. Shared by every account-creating route so the rule
 * lives in one place. Fastify's default ajv config strips unknown keys rather
 * than rejecting; either way an unlisted key never reaches the event.
 */
export const FIRST_TOUCH_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: Object.fromEntries(
    FIRST_TOUCH_KEYS.map((key) => [key, { type: 'string', maxLength: FIRST_TOUCH_MAX_LENGTH }])
  ),
} as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Strip the query string (and, inside a hash fragment, the query string
 * nested there too) from a URL-shaped value, while keeping the hash's path.
 *
 * §6.3.1 defect 1/4: `$current_url`, `$referrer`, `$initial_current_url`,
 * `$initial_referrer`, `$pathname` and `$initial_pathname` are all built from
 * `location.href` / `location.pathname` by posthog-js and can carry
 * `/learn/lesson?w=<base64 worksheet blob>`. Dropping the key is not an option
 * — it is one we need; the problem is entirely in the value. So the fix is
 * value-level: keep the path, drop everything after `?`, in both the
 * "normal" URL and the hash-routed native build
 * (`https://localhost/#/learn/lesson?w=...` → `https://localhost/#/learn/lesson`).
 *
 * Non-string input passes through unchanged — posthog-js properties are
 * loosely typed and this must never throw.
 */
export function sanitizeUrl(value: unknown): unknown {
  if (typeof value !== 'string' || !value) return value;
  const hashIndex = value.indexOf('#');
  const before = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const base = before.split('?')[0];
  if (hashIndex === -1) return base;
  const hash = value.slice(hashIndex + 1);
  const hashPath = hash.split('?')[0];
  return hashPath ? `${base}#${hashPath}` : base;
}

/** Keys posthog-js attaches automatically whose values are URL-shaped (§6.3.1). */
export const URL_SHAPED_KEYS: readonly string[] = [
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$pathname',
  '$initial_pathname',
];

/**
 * Nested `params` allowlist (§6.3.1) — default-deny. Twelve student routes
 * declare `validateSearch`; capturing it wholesale is exactly what would leak
 * `w` (the scanned-worksheet blob), `subchapterTitle` and `title`. Only keys
 * listed here are ever attached, so a new search param is invisible until
 * someone deliberately adds it — the failure mode is a missing property, not
 * a disclosure.
 *
 * Parent routes have no documented search-param contract yet (parent
 * analytics is Phase 2, docs/plans/analytics-implementation-plan.md §7.0) — keep that side empty rather
 * than guessing.
 */
const PARAMS_ALLOWLIST: Readonly<Record<AppName, readonly string[]>> = {
  student: [
    'tab',
    'period',
    'start_mode',
    'default_mode',
    'seed',
    'status',
    'entry',
    'syllabus',
    'section',
    'ama',
    'restart',
    'restartSubchapter',
    'welcomeBack',
    'met',
    'total',
    'start_paper',
    'review_submission',
    'practice_id',
    'start_chapter',
    'start_assignment',
    'chapter',
    'subchapter',
    'topic',
    'resume',
    'view',
  ],
  parent: [],
};

const MAX_PARAM_STRING_LENGTH = 64;

type ParamValue = string | number | boolean;

function coerceParamValue(value: unknown): ParamValue | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return trimmed.length > MAX_PARAM_STRING_LENGTH
      ? trimmed.slice(0, MAX_PARAM_STRING_LENGTH)
      : trimmed;
  }
  // Objects, arrays, null, undefined — never captured. `w` in particular is a
  // string, but this also guards against something structured being coerced
  // into an unbounded JSON string later.
  return undefined;
}

/**
 * Pick only the allowlisted keys out of a route's search object (§6.3.1).
 * Never captured, even if present: `w` (scanned worksheet), `subchapterTitle`,
 * `title` — they simply aren't in the allowlist, so this is a structural
 * guarantee, not a case-by-case check.
 *
 * Returns `undefined` (never `{}`) when nothing survives, so callers don't
 * attach an empty `params` object to every event.
 */
export function pickParams(app: AppName, search: unknown): Record<string, ParamValue> | undefined {
  if (!isPlainObject(search)) return undefined;
  const allowlist = PARAMS_ALLOWLIST[app];
  const out: Record<string, ParamValue> = {};
  for (const key of allowlist) {
    if (!(key in search)) continue;
    const coerced = coerceParamValue(search[key]);
    if (coerced === undefined) continue;
    out[key] = coerced;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * TanStack Router route ids from `apps/student-client/src/routes/` (flat file
 * naming, `_` is the non-nested separator, so `learn/chapters.tsx` is the
 * route id `/learn/chapters`).
 *
 * NOTE: `ama` has NO route — AMA is the bottom-sheet
 * `apps/student-client/src/components/AskSheet.tsx`. It therefore never comes
 * out of this map; AMA events set `feature: 'ama'` explicitly via
 * `setFeature('ama')` or an explicit property.
 */
export const STUDENT_ROUTE_FEATURE: Readonly<Record<string, StudentFeature>> = {
  '/': 'onboarding',
  '/welcome': 'onboarding',
  '/login': 'onboarding',
  '/signup': 'onboarding',
  '/onboarding': 'onboarding',
  '/invite-parent': 'onboarding',

  '/learn': 'learn',
  '/learn/chapters': 'learn',
  '/learn/topics': 'learn',
  '/learn/lesson': 'learn',
  '/learn/chapter/$chapterId': 'learn',
  '/learn/completed': 'learn',
  '/learn/unfinished': 'learn',

  '/practice': 'practice',
  '/practice/chapters': 'practice',
  '/practice/history': 'practice',
  '/practice/history/$chapterId': 'practice',
  '/practice/homework': 'practice',

  '/exam': 'exam',
  '/exam/papers': 'exam',
  '/exam/history': 'exam',

  '/leaderboard': 'leaderboard',
  '/scan': 'scan',

  '/profile': 'profile',
  '/profile/settings': 'profile',
  '/profile/settings/account': 'profile',
  '/profile/settings/plan': 'profile',
  '/profile/settings/school': 'profile',
  '/upgrade-plan': 'profile',
};

/** Parent route templates (`:param` form) from `apps/parent-client/src/lib/analytics-router.ts`. */
export const PARENT_ROUTE_FEATURE: Readonly<Record<string, ParentFeature>> = {
  '/start': 'onboarding',
  '/welcome': 'onboarding',
  '/login': 'onboarding',
  '/signup': 'onboarding',
  '/onboarding/details': 'onboarding',
  '/onboarding/plans': 'onboarding',

  '/': 'children',
  '/children': 'children',
  '/children/:linkId': 'child_hub',
  '/children/:linkId/activity': 'child_hub',

  '/children/:linkId/reports': 'reports',
  '/children/:linkId/reports/:reportId': 'reports',
  '/summary/:linkId': 'reports',

  '/children/:linkId/homework': 'homework',
  '/children/:linkId/homework/:assignmentId': 'homework',
  '/practice': 'homework',

  '/exam': 'exam',
  '/news': 'news',
  '/profile': 'profile',
  '/paywall': 'profile',
};

function normalizeRouteId(routeId: string): string {
  if (!routeId) return '/';
  const withSlash = routeId.startsWith('/') ? routeId : `/${routeId}`;
  // TanStack emits `/learn/` for index-ish ids; drop the trailing slash.
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : '/';
}

/**
 * Longest-prefix, segment-aware lookup, so an unlisted sub-route
 * (`/profile/settings/notifications`) still resolves to its parent's feature
 * instead of leaving a hole in the Q1/Q2 answer (verification step 6).
 */
function lookup<F extends string>(
  map: Readonly<Record<string, F>>,
  routeId: string
): F | undefined {
  const id = normalizeRouteId(routeId);
  const exact = map[id];
  if (exact) return exact;

  let best: F | undefined;
  let bestLength = -1;
  for (const [pattern, feature] of Object.entries(map)) {
    if (pattern === '/') continue;
    if (id.startsWith(`${pattern}/`) && pattern.length > bestLength) {
      best = feature;
      bestLength = pattern.length;
    }
  }
  return best;
}

/**
 * Map a route id (student: TanStack route id; parent: a path already run
 * through `normalizeParentPath`) to the feature it belongs to.
 * `undefined` means genuinely unmatched — treat it as a bug.
 */
export function routeToFeature(app: 'student', routeId: string): StudentFeature | undefined;
export function routeToFeature(app: 'parent', routeId: string): ParentFeature | undefined;
export function routeToFeature(app: AppName, routeId: string): Feature | undefined;
export function routeToFeature(app: AppName, routeId: string): Feature | undefined {
  return app === 'student'
    ? lookup(STUDENT_ROUTE_FEATURE, routeId)
    : lookup(PARENT_ROUTE_FEATURE, normalizeParentPath(routeId));
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Which placeholder a dynamic segment takes, keyed by the segment before it. */
const PARENT_PARAM_BY_PARENT_SEGMENT: Readonly<Record<string, string>> = {
  children: ':linkId',
  summary: ':linkId',
  reports: ':reportId',
  homework: ':assignmentId',
};

/**
 * Parent routes carry real path params. Replace uuid/numeric segments with the
 * router's placeholder so `$current_url` stays low-cardinality — otherwise one
 * route explodes into thousands (the same trap as §6.1(a) on the student side).
 */
export function normalizeParentPath(pathname: string): string {
  const [pathOnly] = pathname.split(/[?#]/);
  const segments = normalizeRouteId(pathOnly).split('/');
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment || segment.startsWith(':')) continue;
    const isDynamic = UUID_RE.test(segment) || /^\d+$/.test(segment);
    if (!isDynamic) continue;
    segments[i] = PARENT_PARAM_BY_PARENT_SEGMENT[segments[i - 1]] ?? ':id';
  }
  const joined = segments.join('/');
  return joined.length > 1 ? joined : '/';
}
